import React, {
  type FC,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { BufferZonePopup } from "../popUp/bufferZonePopup";
import { StripsPopup } from "../popUp/StripsPopup";
import { Button } from "syngenta-digital-cropwise-react-ui-kit";
import {
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  PlusSquareOutlined,
} from "@ant-design/icons";
import { TrialPlotsPopup } from "../popUp/trialPopup";
import { usePlotCardState } from "../utils";
import ReplicantComponent from "../replicantComponent";
import { useNewTrialState } from "../../../../pages/new-trail/hooks/NewTrialStateProvider";
import {
  type AbLine,
  type Plots,
  type BufferZone,
  type MachineryGrid,
  type Replicants,
  type TrialPlot,
  type RenderCardsProps,
  type combineType,
  type NewTrialStateInterface,
} from "../../../../pages/new-trail/types";

import { Flex, Tooltip, Radio } from "antd";
import { formatTranslation } from "../../../utils/translationUtils";
import { useTranslation } from "react-i18next";
import {
  clearStripLinesCache,
  clearStripLinesFromMap,
} from "../../../../maps/components/MapComponent/utils";
import { EditButton } from "../../EditButton";
import { formatAreaInHectares } from "../../../utils/areaCalculation";
import { AbLinePopup } from "../popUp/abLinePopup";

export const RenderCards: FC<RenderCardsProps> = ({
  cards,
  toggleVisibility,
  data,
  setIsModalOpen,
  setDeleteData,
  trialData,
  isStripType,
  viewMode = "plots",
  onViewModeChange,
}) => {
  const {
    handleBufferZoneData,
    handleReplicantData,
    replicantData,
    setReplicantData,
    handleAlignmentSelectionChange,
    handleTrailPlotsData,
    getAbLineColor,
  } = usePlotCardState();
  const { t } = useTranslation();

  const { newTrialState, setNewTrialState } = useNewTrialState();
  const [activeAbLinePopupId, setActiveAbLinePopupId] = useState<number | null>(
    null
  );

  const isTrialCard = data.name === "Trial Plots";

  const effectiveViewMode = viewMode ?? "plots";

  const isStripView = Boolean(isStripType) && effectiveViewMode === "strips";

  useEffect(() => {
    setNewTrialState((prevState: any) => {
      return {
        ...prevState,
        trial_plots: prevState.trial_plots.map(
          (plotData: TrialPlot, i: number) => {
            return {
              ...plotData,
              plot: {
                ...plotData.plot,
                isEditing: false,
              },
            };
          }
        ),
      };
    });
  }, []);

  const formatAbLineWidth = (abLine: AbLine): string => {
    const width = abLine.grid_width ?? abLine.gridWidth;
    if (typeof width !== "number" || Number.isNaN(width)) {
      return "-";
    }

    return `${width}m`;
  };

  // The strips Radio below is this function's only caller and a radio only
  // ever fires on selection, so there is no "unselected" case to handle.
  const handleStripSelectionChange = (index: number): void => {
    if (!isStripType) {
      return;
    }

    setNewTrialState((prevState: NewTrialStateInterface) => {
      // Single selection logic: only the selected index gets true, all others get false
      const updatedAbLines = prevState.ab_line.map(
        (abLine: AbLine, currentIndex: number) => ({
          ...abLine,
          selectedForStrips: currentIndex === index, // Only the selected index gets true
        })
      );

      const selectedLine = updatedAbLines[index];
      const rawWidth = selectedLine?.grid_width ?? 50;
      const spacing = rawWidth;

      // Initialize or update strips data with AB-line selection
      const count = prevState.stripsData?.count ?? 2; // Default to 2 strips if no existing data

      // Don't generate simple offsets here - let StripsPopup handle smart calculation
      // This prevents caching of simple offsets that override smart calculations
      const offsets: number[] = [0]; // Placeholder - will be recalculated by StripsPopup

      const updatedStripsData = {
        isStripMode: true,
        appliedFromPopup: false, // Flag to indicate not yet applied from popup
        count,
        selectedAbLineIndex: index,
        offsets,
        spacing,
        createdAt: prevState.stripsData?.createdAt ?? new Date().toISOString(),
        version: "1.0",
        // Store AB-line details to avoid dependency on ab_line array
        selectedAbLine: {
          name: selectedLine.name,
          gridWidth: rawWidth,
          geojson: selectedLine.geojson,
        },
      };

      // Force recalculation of trial plots based on new strip configuration
      const updatedTrialPlots = prevState.trial_plots.map((trialPlot) => {
        const updatedPlot = {
          ...trialPlot,
          plot: {
            ...trialPlot.plot,
            geometrySource: "manual", // Mark for recalculation
            // Force geometry update by clearing existing geometry
            geojson: {
              type: "Feature",
              geometry: {
                type: "Point", // Reset to Point to trigger recalculation
                coordinates: [0, 0],
              },
            },
          },
        };

        // Ensure the plot is marked as a strip plot
        updatedPlot.plot.isStripPlot = true;
        updatedPlot.plot.totalStrips = count;
        updatedPlot.plot.stripIndices = Array.from(
          { length: count },
          (_, i) => i
        );

        return updatedPlot;
      });

      return {
        ...prevState,
        ab_line: updatedAbLines,
        stripsData: updatedStripsData,
        trial_plots: updatedTrialPlots,
      };
    });

    // Clear strip lines and cache to force complete recalculation
    clearStripLinesFromMap();
    clearStripLinesCache();

    // Additional aggressive cache clearing with delay to ensure state update
    setTimeout(() => {
      clearStripLinesCache();
    }, 50);
  };

  const renderStripsSummary = (): ReactNode => {
    const stripsData = newTrialState?.stripsData;
    const stripDetails = stripsData?.stripDetails ?? [];

    if (!stripDetails.length) {
      return (
        <div className="strips-section">
          <div className="strips-summary-placeholder">
            Select an A/B line to calculate strip layout and view details here.
          </div>
        </div>
      );
    }

    return (
      <div className="strips-section">
        <div
          className="strips-edit-section"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "4px",
          }}
        >
          <StripsPopup
            openPopoverKey="openPopover"
            onHandleData={handleTrailPlotsData}
            val={data.id}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                border: "none",
                background: "transparent",
                color: "#666",
                gap: "1px",
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: "4px",
              }}
            >
              <span>Edit</span>
              <EditButton from="no-tooltip" />
            </div>
          </StripsPopup>
        </div>
        <div className="strips-table">
          <table>
            <thead>
              <tr>
                <th>Strip</th>
                <th>Length</th>
                <th>Width</th>
                <th>Area</th>
              </tr>
            </thead>
            <tbody>
              {stripDetails.map((strip, index) => (
                <tr key={strip.index ?? index}>
                  <td className="first-col">Strip {index + 1}</td>
                  <td>
                    {strip.stripLength !== undefined &&
                    strip.stripLength !== null
                      ? `${strip.stripLength.toFixed(1)} m`
                      : "-"}
                  </td>
                  <td>
                    {strip.stripWidth !== undefined && strip.stripWidth !== null
                      ? `${strip.stripWidth.toFixed(1)} m`
                      : "-"}
                  </td>
                  <td>{formatAreaInHectares(strip.area)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const delButton = (val: combineType): ReactNode => {
    return (
      <Tooltip title={formatTranslation(t("trail.deleteSection"))}>
        <Button
          data-testid={`delete-button-${val.id as number}`}
          className="del-btn"
          onClick={() => {
            setIsModalOpen(true);
            setDeleteData(val);
          }}
        >
          <DeleteOutlined alt="del-icon" />
        </Button>
      </Tooltip>
    );
  };

  const filteredCards = useMemo(() => {
    return cards?.filter((val) => {
      if (val && typeof val === "object") {
        if (
          ("plot" in val && val.plot.key === data.name) ||
          ("key" in val && val.key === data.name)
        ) {
          return true;
        }
      }
      return false;
    });
  }, [cards, data.name]);

  const cardsToRender = useMemo(() => {
    if (data.name === "A/B Line + Machinery Grid") {
      return (newTrialState?.ab_line ?? []).filter(
        (line: any) => line?.key === "A/B Line + Machinery Grid"
      );
    }

    return filteredCards ?? [];
  }, [data.name, filteredCards, newTrialState?.ab_line]);

  const renderActionButton = (
    genericPlot: any,
    trialPlot: any,
    cardDetail: any,
    i: number
  ): ReactNode => {
    if (data.name === "Buffer Zone") {
      return (
        <BufferZonePopup
          onHandleData={handleBufferZoneData}
          // openPopoverKey={id ? "" : "openEditPopover"}
          openPopoverKey="openEditPopover"
          val={genericPlot.id}
        >
          <Button>
            <EditButton from="no-tooltip" />
          </Button>
        </BufferZonePopup>
      );
    }
    // else if (data.name === "Machinery Grid") {
    //   return (
    //     <MachineryGridPopup
    //       onHandleData={handleMachineGridData}
    //       // openPopoverKey={id ? "" : "openEditPopover"}
    //       openPopoverKey="openEditPopover"
    //       val={genericPlot.id}
    //       trialData={trialData}
    //     >
    //       <Button>
    //         <EditOutlined />
    //       </Button>
    //     </MachineryGridPopup>
    //   );
    // }
    else if (data.name === "Trial Plots") {
      return (
        <>
          <TrialPlotsPopup
            openPopoverKey="openEditPopover"
            onHandleData={handleTrailPlotsData}
            val={trialPlot.plot.id}
            indexKey={i}
          >
            <Tooltip title={formatTranslation(t("trail.modifyPlot"))}>
              <Button>
                <EditButton from="no-tooltip" />
              </Button>
            </Tooltip>
          </TrialPlotsPopup>
          <Tooltip title={formatTranslation(t("trail.copyTreatment"))}>
            <Button
              className="add-replicant"
              onClick={() => {
                handleReplicantData(cardDetail, i);
              }}
            >
              <PlusSquareOutlined />
            </Button>
          </Tooltip>
        </>
      );
    } else {
      return <></>;
    }
  };

  if (isTrialCard && isStripView) {
    return <>{renderStripsSummary()}</>;
  }

  /**
   * Converts "Plots 1" to "Plots A", "Plots 2" to "Plots B", etc.
   */
  function plotNameToLabel(name: string): string {
    const match = /^Plots\s*(\d+)$/.exec(name);
    if (!match) return name;

    const num = Number.parseInt(match[1], 10);
    if (Number.isNaN(num) || num < 1) return name;

    let result = "";
    let n = num;

    while (n > 0) {
      n--;
      result = String.fromCodePoint(65 + (n % 26)) + result;
      n = Math.floor(n / 26);
    }

    return `${formatTranslation(
      t("trials.plotDesign.trialPlots.table.header1")
    )} ${result}`;
  }

  return (
    <>
      {data.name === "A/B Line + Machinery Grid" &&
        cardsToRender.length > 0 && (
          <div className="abline-table-header">
            <span>Name</span>
            <span>Width</span>
          </div>
        )}
      {cardsToRender.map(
        (
          cardDetail:
            | AbLine
            | BufferZone
            | MachineryGrid
            | TrialPlot
            | Replicants
            | Plots,
          i
        ): ReactNode => {
          const trialPlot = cardDetail as TrialPlot;
          const genericPlot = cardDetail as
            | AbLine
            | BufferZone
            | MachineryGrid
            | Replicants
            | Plots;

          return (
            <React.Fragment key={`${data.name}-${i}`}>
              <div className="open-sec">
                <div className="open-sec-title">
                  {data.name === "Trial Plots" ? (
                    plotNameToLabel(trialPlot.plot.name)
                  ) : (
                    <Flex vertical={false} className="open-sec-title-circle">
                      {data.name === "A/B Line + Machinery Grid" ? (
                        <div className="abline-row-content">
                          <div className="abline-name-column">
                            <span
                              className={`abline-color-dot ${
                                getAbLineColor(
                                  genericPlot.id,
                                  "className"
                                ) as string
                              }`}
                              aria-hidden="true"
                            />
                            <span className="abline-name-text">
                              {newTrialState?.ab_line[i]?.name ?? "A/B Line"}
                            </span>
                          </div>
                        </div>
                      ) : (
                        genericPlot.name
                      )}
                    </Flex>
                  )}

                  {"size" in cardDetail && (
                    <span>
                      •{" "}
                      {cardDetail.type === "EXTERNAL" ? "External" : "Internal"}{" "}
                      {cardDetail.size}
                    </span>
                  )}

                  {"plot" in cardDetail &&
                    (!isStripType || effectiveViewMode === "plots") && (
                      <span>
                        • {trialPlot?.plot.rows * trialPlot?.plot.columns} plot{" "}
                        {trialPlot?.plot.rows} x {trialPlot?.plot.columns}|
                        {trialPlot?.plot.column_size}m x{" "}
                        {trialPlot?.plot.row_size}m
                      </span>
                    )}
                </div>
                <div className="action-sec default-action-sec">
                  {data.name === "A/B Line + Machinery Grid" ? (
                    <>
                      <span className="abline-width-text">
                        {formatAbLineWidth(
                          newTrialState?.ab_line?.[i] ?? (genericPlot as AbLine)
                        )}
                      </span>
                      <div className="abline-actions">
                        <Tooltip
                          title={formatTranslation(t("trail.toggleVisibility"))}
                        >
                          <Button
                            onClick={() => {
                              toggleVisibility(i, data);
                            }}
                          >
                            {genericPlot?.visible ??
                            trialPlot?.plot?.visible ? (
                              <EyeOutlined />
                            ) : (
                              <EyeInvisibleOutlined />
                            )}
                          </Button>
                        </Tooltip>
                        <Tooltip
                          title={formatTranslation(t("trail.deleteSection"))}
                        >
                          <Button
                            className="abline-delete-btn"
                            onClick={() => {
                              setIsModalOpen(true);
                              setDeleteData(genericPlot as combineType);
                            }}
                          >
                            <DeleteOutlined />
                          </Button>
                        </Tooltip>
                        <AbLinePopup
                          open={
                            activeAbLinePopupId === (genericPlot.id as number)
                          }
                          abLineId={genericPlot.id as number}
                          onClose={() => {
                            setActiveAbLinePopupId(null);
                          }}
                        >
                          <Tooltip
                            title={formatTranslation(t("trail.modifyPlot"))}
                          >
                            <Button
                              onClick={() => {
                                setActiveAbLinePopupId(
                                  genericPlot.id as number
                                );
                              }}
                            >
                              <EditButton from="no-tooltip" />
                            </Button>
                          </Tooltip>
                        </AbLinePopup>
                      </div>
                    </>
                  ) : (
                    <>
                      {data.name !== "Machinery Grid" && (
                        <div className="eye-edit">
                          {renderActionButton(
                            genericPlot,
                            trialPlot,
                            cardDetail,
                            i
                          )}
                          <Tooltip
                            title={formatTranslation(
                              t("trail.toggleVisibility")
                            )}
                          >
                            <Button
                              onClick={() => {
                                toggleVisibility(i, data);
                              }}
                            >
                              {genericPlot?.visible ??
                              trialPlot?.plot?.visible ? (
                                <EyeOutlined />
                              ) : (
                                <EyeInvisibleOutlined />
                              )}
                            </Button>
                          </Tooltip>
                        </div>
                      )}

                      <div className="del-right">
                        {delButton(
                          trialPlot?.plot?.key === "Trial Plots"
                            ? trialPlot.plot
                            : (genericPlot as combineType)
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
              {data.name === "A/B Line + Machinery Grid" && (
                <div className="abline-row-options">
                  {viewMode === "strips" ? (
                    <Radio
                      checked={
                        newTrialState?.ab_line[i]?.selectedForStrips ?? false
                      }
                      onChange={() => {
                        handleStripSelectionChange(i);
                      }}
                    >
                      <span className="abline-strip-checkbox-label">
                        {formatTranslation(t("trail.ablineSelectForStrips"))}
                      </span>
                    </Radio>
                  ) : (
                    <Radio
                      checked={
                        newTrialState?.ab_line[i]?.selectedForAlignment ??
                        i === 0
                      }
                      onChange={() => {
                        handleAlignmentSelectionChange(i);
                      }}
                    >
                      <span className="abline-strip-checkbox-label">
                        {formatTranslation(
                          t("trail.ablineUseForPlotAlignment")
                        )}
                      </span>
                    </Radio>
                  )}
                </div>
              )}

              {data?.name === "Trial Plots" &&
                effectiveViewMode === "plots" &&
                newTrialState?.trial_plots[i]?.replicants?.map(
                  (valData: Replicants, index: number) => {
                    return (
                      <ReplicantComponent
                        valData={valData}
                        key={`${data?.name}-${valData?.id as number}-${index}`}
                        index={index}
                        replicantData={replicantData}
                        toggleVisibility={toggleVisibility}
                        setReplicantData={setReplicantData}
                        delButton={delButton}
                      />
                    );
                  }
                )}
            </React.Fragment>
          );
        }
      )}
    </>
  );
};
